<template>
  <div
    v-if="filesByCategory"
    v-on-click-outside="() => (showMenu = false)"
    class="flex items-center justify-between pt-3"
  >
    <label
      class="text-sm whitespace-nowrap text-headline"
      data-cy="attach-documents"
    >
      {{ $t("DEALS.DOCUMENTS.ATTACH_DOCUMENT") }}
    </label>

    <button
      type="button"
      class="relative cursor-default rounded-sm p-2 text-left text-sm focus:outline-hidden"
      data-cy="attach-documents-button"
      :class="{ 'bg-blue-100 text-blue-500': showMenu, 'bg-white': !showMenu }"
      @click="toggleMenu"
    >
      <div class="cursor-pointer">
        <icon-base :icon="IconAdd" :width="20" :height="20" />
      </div>
    </button>

    <div
      v-show="showMenu"
      data-cy="attach-documents-modal"
      class="absolute right-7 bottom-56 h-full max-h-100 w-240 items-center justify-between overflow-auto bg-white p-4 shadow-xl"
    >
      <div class="flex h-72 flex-col">
        <div class="flex items-center justify-between">
          <p class="text-lg font-bold text-headline">
            {{ $t("DEALS.DOCUMENTS.ADD_DOCUMENTS") }}
          </p>
          <div>
            <lf-checkbox
              v-model="isAllSelected"
              class="text-primary"
              :value="isAllSelected"
              name="selectAll"
              @change="toggleSelectAll"
            >
              <label
                class="cursor-pointer text-primary"
                for="attachments-select-all"
              >
                {{ $t("COMMON.SELECT_ALL") }}
              </label>
            </lf-checkbox>
          </div>
        </div>
        <div
          class="grid grid-flow-row grid-cols-3 gap-10 overflow-y-auto px-1 py-4"
        >
          <div
            v-for="file in allFiles"
            :key="file.id"
            class="flex items-start break-all"
          >
            <div class="flex flex-col space-y-1">
              <input
                :id="`attachment-${file.id}`"
                v-model="attachments"
                class="cursor-pointer"
                type="checkbox"
                :value="file.id"
              />
              <div>
                <icon-base
                  height="25"
                  width="25"
                  :icon="getIcon(file.original_name)"
                />
              </div>
            </div>

            <label
              :for="`attachment-${file.id}`"
              class="cursor-pointer select-none"
            >
              <span class="text-primary">{{ file.original_name }}</span>
              <p class="font-normal text-label">
                {{ getFileName(file.file_type) }}
              </p>
            </label>
          </div>
        </div>
      </div>
      <div class="mt-4 flex items-center justify-between border-t pt-4">
        <button
          type="button"
          class="mt-2 font-semibold text-blue-500 focus:outline-hidden"
          @click="showDocumentUploadModal = true"
        >
          {{ $t("DEALS.DOCUMENTS.ADD_NEW_DOCUMENT") }}
        </button>
      </div>
    </div>
  </div>
  <div class="flex h-16 flex-wrap items-center justify-start overflow-y-auto">
    <lf-badge
      v-for="file in selectedAttachments"
      :key="file.id"
      removable
      component-style="attachDocumentStyle"
      data-cy="attachment-badge"
      @click="removeAttachment(file)"
    >
      {{ file.original_name }}
    </lf-badge>
  </div>
  <document-upload-modal
    v-if="showDocumentUploadModal"
    :close="handleModalClose"
  />
</template>
<script setup lang="ts">
import { type Component, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { SIGNED_DOC_FILE_TYPE } from "@/helpers/constants";

import DocumentUploadModal from "@/components/deals/documents/DocumentUploadModal.vue";
import IconAdd from "@/components/icons/deals/IconAdd.vue";
import IconDocSmall from "@/components/icons/fileTypes/IconDocSmall.vue";
import IconImgSmall from "@/components/icons/fileTypes/IconImgSmall.vue";
import IconPdfSmall from "@/components/icons/fileTypes/IconPdfSmall.vue";
import IconXlsSmall from "@/components/icons/fileTypes/IconXlsSmall.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfBadge from "@/components/ui/LfBadge.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";

import type { IFile } from "@/models/common";
import type { FileTypeCollection } from "@/models/options";

const props = defineProps<{
  filesByCategory: Record<string, IFile[]>;
  bankStatmentTypes?: FileTypeCollection;
}>();

const attachments = defineModel<number[]>({ required: true });

const { getters } = useStore();
const { t } = useI18n();

const showMenu = ref(false);

const icons = {
  pdf: IconPdfSmall,
  doc: IconDocSmall,
  xls: IconXlsSmall,
  document: IconDocSmall,
  image: IconImgSmall,
};
enum Extension {
  pdf = "pdf",
  doc = "doc",
  docx = "docx",
  xls = "xls",
  xlsx = "xlsx",
  png = "png",
  jpg = "jpg",
  jpeg = "jpeg",
  heic = "heic",
}

type FileEntry = {
  name: string;
  value: string;
};

const fileTypes = computed<Record<number, FileTypeCollection>>(
  () => getters["options/all"]["file_types"]
);

const typeDropdownOptions = computed(() =>
  Object.values(fileTypes.value).reduce(
    (accumulator: FileEntry[], typeCollection) => {
      const parsedTypes: FileEntry[] = typeCollection.types.map((type) => ({
        name: t(`FILE_TYPES.${type}`),
        value: type,
      }));
      accumulator.push(...parsedTypes);
      return accumulator;
    },
    []
  )
);

const getFileName = (type: string) => {
  return typeDropdownOptions.value.find((el) => el.value === type)?.name;
};

const showDocumentUploadModal = ref(false);

const allFiles = computed(() => {
  return Object.values(props.filesByCategory).flat();
});

const isAllSelected = computed(
  () => attachments.value.length === allFiles.value.length
);
const selectedAttachments = computed(() => {
  if (!attachments.value.length) {
    return [];
  }

  const files = allFiles.value.filter((file) =>
    attachments.value.includes(file.id)
  );

  const sortDocuments = files.some(
    (file) =>
      file.file_type === SIGNED_DOC_FILE_TYPE ||
      props.bankStatmentTypes?.types.includes(file.file_type)
  );

  if (!sortDocuments) {
    return files;
  }

  const signedDocuments = files.filter(
    (file) => file.file_type === SIGNED_DOC_FILE_TYPE
  );

  const bankDocuments = files.filter((file) =>
    props.bankStatmentTypes?.types.includes(file.file_type)
  );

  const otherDocuments = files.filter(
    (file) =>
      file.file_type !== SIGNED_DOC_FILE_TYPE &&
      !props.bankStatmentTypes?.types.includes(file.file_type)
  );

  return [...signedDocuments, ...bankDocuments, ...otherDocuments];
});

const getIcon = (fileName: string) => {
  const extension = fileName.split(".")[1] as Extension;
  const iconmap: Record<Extension, Component> = {
    [Extension.doc]: icons.doc,
    [Extension.pdf]: icons.pdf,
    [Extension.xls]: icons.xls,
    [Extension.xlsx]: icons.xls,
    [Extension.docx]: icons.document,
    [Extension.png]: icons.image,
    [Extension.jpg]: icons.image,
    [Extension.jpeg]: icons.image,
    [Extension.heic]: icons.image,
  };

  return iconmap[extension] || icons.document;
};
const removeAttachment = (file: IFile) => {
  const { id } = file;
  attachments.value = attachments.value.filter((file) => file !== id);
};

const toggleMenu = () => {
  showMenu.value = !showMenu.value;
};
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    attachments.value = [];
    return;
  }
  attachments.value = allFiles.value.map((file) => file.id);
};

const handleModalClose = () => {
  showDocumentUploadModal.value = false;
};
</script>
